import { Extension } from '@tiptap/core';
import { InputRule } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { generateBlockId } from './blockContext';
import type { HandlebarsBlockType } from '../../types';

// Permissive regex patterns for freeform mode (used when allowFreeformHandlebars is enabled)
// Captures any content between {{ and }} (excluding nested braces)
const FREEFORM_INLINE_REGEX = /\{\{(?![#/])([^{}]+)\}\}$/;

// Block start with any arguments: {{#getProposal id}}, {{#if eq a b}}, {{#helper arg1 "string"}}
const FREEFORM_BLOCK_START_REGEX = /\{\{#([^\s{}]+)(?:\s+([^{}]*))?\}\}$/;

// Block end: {{/anything}}
const FREEFORM_BLOCK_END_REGEX = /\{\{\/([^\s{}]+)\}\}$/;

export interface HandlebarsInputRulesOptions {
  /** When true, allows any Handlebars syntax without strict validation (default: false) */
  allowFreeformHandlebars?: boolean;
}

/**
 * Tiptap Extension that provides InputRules for automatic Handlebars tag conversion.
 *
 * Allows users to type Handlebars syntax directly and have it automatically
 * converted to proper nodes when they finish typing `}}`.
 */
export const HandlebarsInputRules = Extension.create<HandlebarsInputRulesOptions>({
  name: 'handlebarsInputRules',

  addOptions() {
    return {
      allowFreeformHandlebars: false,
    };
  },

  addInputRules() {
    const { allowFreeformHandlebars } = this.options;

    // When freeform mode is disabled, don't add any InputRules.
    // This ensures users must use the autocomplete provider for validation.
    if (!allowFreeformHandlebars) {
      return [];
    }

    return [
      // Rule 1: Inline tags - {{variable}}
      new InputRule({
        find: FREEFORM_INLINE_REGEX,
        handler: ({ state, range, match }) => {
          const variable = match[1].trim();
          const { tr, schema } = state;

          const node = schema.nodes.handlebarsTag.create({
            variable,
            description: null,
            isValid: true,
            error: null,
          });

          tr.replaceWith(range.from, range.to, node);
        },
      }),

      // Rule 2: Block start tags - {{#if condition}}
      // Creates a wrapping handlebarsBlock node containing an empty paragraph
      new InputRule({
        find: FREEFORM_BLOCK_START_REGEX,
        handler: ({ state, range, match }) => {
          const blockType = match[1] as HandlebarsBlockType;
          const variable = (match[2] || '').trim();
          const blockId = generateBlockId();
          const { tr, schema } = state;

          const blockNode = schema.nodes.handlebarsBlock.create(
            {
              blockType,
              variable,
              blockId,
              description: null,
              isValid: true,
              error: null,
              nonEditable: false,
            },
            schema.nodes.paragraph.create()
          );

          tr.replaceWith(range.from, range.to, blockNode);

          // Position cursor inside the block's empty paragraph
          // range.from + 1 (block open) + 1 (paragraph open) = inside the paragraph
          const cursorPos = range.from + 2;
          tr.setSelection(TextSelection.create(tr.doc, cursorPos));
        },
      }),

      // Rule 3: Block end tags - {{/if}}
      // Instead of inserting an end node, exit the nearest ancestor block of that type
      new InputRule({
        find: FREEFORM_BLOCK_END_REGEX,
        handler: ({ state, range, match }) => {
          const blockType = match[1] as HandlebarsBlockType;
          const { tr, schema } = state;
          const $pos = state.doc.resolve(range.from);

          // Find the nearest ancestor handlebarsBlock of this type
          for (let depth = $pos.depth; depth > 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === 'handlebarsBlock' && node.attrs.blockType === blockType) {
              // Delete the typed text
              tr.delete(range.from, range.to);

              // Move cursor after the block
              const afterBlock = $pos.after(depth);
              const adjustment = range.to - range.from;
              const adjustedPos = afterBlock - adjustment;

              // Ensure there's a paragraph after the block to land in
              const resolvedPos = Math.min(adjustedPos, tr.doc.content.size);
              const $afterPos = tr.doc.resolve(resolvedPos);
              if ($afterPos.nodeAfter === null || $afterPos.parent.type.name === 'doc') {
                tr.insert(resolvedPos, schema.nodes.paragraph.create());
                tr.setSelection(TextSelection.create(tr.doc, resolvedPos + 1));
              } else {
                tr.setSelection(TextSelection.create(tr.doc, resolvedPos));
              }
              return;
            }
          }

          // No matching block found — just delete the typed text
          tr.delete(range.from, range.to);
        },
      }),
    ];
  },
});
